import React, { useState, useEffect, useCallback } from 'react';
import { Clock, CheckCircle, Package, Truck, Search, RefreshCw, Phone } from 'lucide-react';
import { supabase } from '../../utils/supabase';

const tabs = [
  { key: 'all', label: 'All', icon: Package },
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'ready', label: 'Ready', icon: Package },
  { key: 'delivered', label: 'Delivered', icon: Truck },
];

const statusStyles = {
  pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-blue-100 text-blue-700',
  ready: 'bg-green-100 text-green-700',
  delivered: 'bg-gray-100 text-gray-600',
};

const statusEmoji = {
  pending: '🟡',
  confirmed: '🔵',
  ready: '🟢',
  delivered: '✅',
};

const nextStatus = {
  pending: 'confirmed',
  confirmed: 'ready',
  ready: 'delivered',
};

const nextStatusLabel = {
  pending: '✅ Confirm Order',
  confirmed: '🍳 Mark Ready',
  ready: '🚗 Mark Delivered',
};

const nextStatusColor = {
  pending: 'bg-blue-500 hover:bg-blue-600',
  confirmed: 'bg-green-500 hover:bg-green-600',
  ready: 'bg-gray-500 hover:bg-gray-600',
};

export default function Orders() {
  const [activeTab, setActiveTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOrders = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: rest } = await supabase
        .from('restaurants')
        .select('id')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurantId(rest.id);

      const { data: ordersData } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', rest.id)
        .order('created_at', { ascending: false });

      setOrders(ordersData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Real-time subscription
  useEffect(() => {
    if (!restaurantId) return;
    const channel = supabase
      .channel('orders-page')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${restaurantId}`
      }, () => {
        fetchOrders(true);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [restaurantId, fetchOrders]);

  const updateStatus = async (id, currentStatus) => {
    const next = nextStatus[currentStatus];
    if (!next) return;

    setUpdatingId(id);
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status: next } : o));

    const { error } = await supabase
      .from('orders')
      .update({ status: next })
      .eq('id', id);

    if (error) {
      console.error(error);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: currentStatus } : o));
    }
    setUpdatingId(null);
  };

  const filtered = orders.filter(o => {
    const matchTab = activeTab === 'all' || o.status === activeTab;
    const matchSearch =
      (o.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_phone || '').includes(search) ||
      o.id.includes(search);
    return matchTab && matchSearch;
  });

  const timeAgo = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const pendingCount = orders.filter(o => o.status === 'pending').length;

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-32 mb-3" />
            <div className="h-3 bg-gray-100 rounded w-48 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-64" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Orders</h2>
          <p className="text-sm text-gray-400 mt-0.5">{orders.length} total orders</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white w-40"
            />
          </div>
          <button
            onClick={() => fetchOrders(true)}
            disabled={refreshing}
            className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
          >
            <RefreshCw size={16} className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🛎️</span>
          <p className="text-orange-700 font-medium text-sm">
            {pendingCount} new {pendingCount === 1 ? 'order' : 'orders'} waiting for confirmation!
          </p>
          <button
            onClick={() => setActiveTab('pending')}
            className="ml-auto text-orange-600 text-xs font-semibold border border-orange-200 px-3 py-1.5 rounded-lg hover:bg-orange-100 transition"
          >
            View →
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(({ key, label, icon: Icon }) => {
          const count = key === 'all' ? orders.length : orders.filter(o => o.status === key).length;
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all
                ${isActive ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              <Icon size={15} />
              {label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-white/20 text-white' : key === 'pending' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-gray-100">
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package size={28} className="text-gray-200" />
            </div>
            <p className="text-gray-400 font-medium">No orders found</p>
            <p className="text-gray-300 text-sm mt-1">
              {orders.length === 0 ? 'Orders from WhatsApp will appear here in real-time' : 'Try a different filter or search'}
            </p>
          </div>
        ) : (
          filtered.map(order => {
            const items = Array.isArray(order.items) ? order.items : [];
            const customerDisplay = order.customer_name && order.customer_name !== order.customer_phone
              ? order.customer_name
              : null;

            return (
              <div
                key={order.id}
                className={`bg-white rounded-2xl border shadow-sm p-5 transition ${order.status === 'pending' ? 'border-orange-200' : 'border-gray-100'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">

                    {/* Order Header */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-bold text-gray-800 text-sm">#{order.id.slice(0, 8)}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize flex items-center gap-1 ${statusStyles[order.status] || 'bg-gray-100 text-gray-600'}`}>
                        {statusEmoji[order.status]} {order.status}
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(order.created_at)}</span>
                    </div>

                    {/* Customer Info */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                        <span className="text-primary font-bold text-xs">
                          {(order.customer_phone || '?').slice(-2)}
                        </span>
                      </div>
                      <div>
                        {customerDisplay && (
                          <p className="text-sm font-medium text-gray-700">{customerDisplay}</p>
                        )}
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Phone size={10} />
                          +{order.customer_phone}
                          {order.address && ` · ${order.address}`}
                        </p>
                      </div>
                    </div>

                    {/* Items */}
                    <div className="flex flex-wrap gap-1.5">
                      {items.length > 0 ? items.map((item, i) => (
                        <span key={i} className="bg-gray-50 border border-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-lg">
                          {item.name} ×{item.qty}
                        </span>
                      )) : (
                        <span className="text-xs text-gray-300 italic">No items recorded</span>
                      )}
                    </div>
                  </div>

                  {/* Right Side */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="font-bold text-gray-800 text-lg">
                      SAR {order.total || 0}
                    </span>
                    {nextStatus[order.status] && (
                      <button
                        onClick={() => updateStatus(order.id, order.status)}
                        disabled={updatingId === order.id}
                        className={`text-white text-xs font-medium px-4 py-2 rounded-xl transition disabled:opacity-60 ${nextStatusColor[order.status] || 'bg-primary hover:bg-secondary'}`}
                      >
                        {updatingId === order.id ? '...' : nextStatusLabel[order.status]}
                      </button>
                    )}
                    {order.status === 'delivered' && (
                      <span className="text-xs text-green-600 font-medium">✅ Completed</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}